/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - SLA Blast Radius Dashboard
 * Feature #117: Operational Dashboards - User Story 9
 * Constitution: v1.12.0
 * Conformed to Spec 197 design blueprint
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Shield, AlertTriangle, Server, Users, Building, Database, Layers } from 'lucide-react';
import { useSla } from '../../hooks/operations/useSla';
import {
  useDatasources,
  useDatasourceBlastRadius,
  useLakehouseWarehouseAssets,
  useAssetBlastRadius,
  useDependencyNetwork,
  type DatasourceItem,
  type LhWhAsset,
} from '../../services/operations/slaService';
import {
  PageLayout,
  Card,
  StatCard,
  LoadingState,
} from '../../components/ui';
import {
  ExportButton,
  BlastRadiusGraph,
  DependencyMap,
  AlertTimeline,
} from '../../components/operations';

/**
 * Lakehouse / warehouse failure impact — real data from collected lineage. "If this
 * lakehouse/warehouse is unavailable, which datasets/workspaces/capacities/clients break?"
 * Resolved via datasets reading the asset's SQL endpoint.
 */
const LakehouseWarehouseImpactPanel: React.FC = () => {
  const { data: assets = [], isLoading: assetsLoading } = useLakehouseWarehouseAssets();
  const [selectedIdx, setSelectedIdx] = useState<number | ''>('');
  const selected = selectedIdx === '' ? null : assets[selectedIdx];
  const { data: impact, isLoading: impactLoading } = useAssetBlastRadius(
    selected ? { kind: selected.kind, id: selected.id } : null
  );

  const label = (a: LhWhAsset) =>
    `${a.kind === 'lakehouse' ? 'Lakehouse' : 'Warehouse'}: ${a.name} — ${a.consumer_models} dataset${a.consumer_models === 1 ? '' : 's'}`;

  return (
    <Card padding="lg">
      <div className="mb-2 flex items-center gap-2">
        <Layers className="h-5 w-5 text-[var(--color-accent)]" />
        <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Lakehouse / Warehouse Failure Impact</h2>
      </div>
      <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
        If a lakehouse or warehouse is unavailable, which datasets, workspaces, capacities, and clients depend on it — derived from collected lineage (datasets reading its SQL endpoint).
      </p>

      <div className="mb-4 max-w-2xl">
        <label className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">Lakehouse / Warehouse</label>
        <select
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
          value={selectedIdx}
          onChange={(e) => setSelectedIdx(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={assetsLoading}
        >
          <option value="">{assetsLoading ? 'Loading…' : `Select a lakehouse or warehouse (${assets.length})`}</option>
          {assets.map((a, i) => (
            <option key={`${a.kind}|${a.id}`} value={i}>{label(a)}</option>
          ))}
        </select>
      </div>

      {selected && impactLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
        </div>
      )}

      {selected && impact && !impactLoading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Affected Datasets" value={impact.affected_datasets} icon={<Database className="h-5 w-5 text-[var(--color-accent)]" />} />
            <StatCard label="Affected Workspaces" value={impact.affected_workspaces} icon={<Building className="h-5 w-5 text-[var(--color-status-warning)]" />} />
            <StatCard label="Affected Capacities" value={impact.affected_capacities} icon={<Server className="h-5 w-5 text-violet-500" />} />
            <StatCard label="Affected Clients" value={impact.affected_clients} icon={<Users className="h-5 w-5 text-[var(--color-status-warning)]" />} />
          </div>

          {impact.workspaces.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
              <table className="w-full text-sm">
                <thead className="border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-[var(--color-text-secondary)]">Workspace</th>
                    <th className="px-4 py-2 text-left font-medium text-[var(--color-text-secondary)]">Capacity</th>
                    <th className="px-4 py-2 text-right font-medium text-[var(--color-text-secondary)]">Datasets using it</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                  {impact.workspaces.map((w) => (
                    <tr key={w.workspace_id}>
                      <td className="px-4 py-2 font-medium text-[var(--color-text-primary)]">{w.workspace_name}</td>
                      <td className="px-4 py-2 text-[var(--color-text-secondary)]">{w.capacity_name || '—'}</td>
                      <td className="px-4 py-2 text-right text-[var(--color-text-secondary)]">{w.dataset_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

/**
 * Datasource failure impact — real data from collected lineage (dbo.dataset_datasources).
 * "If this external datasource is down, which datasets/workspaces/capacities/clients break?"
 */
const DatasourceImpactPanel: React.FC = () => {
  const { data: datasources = [], isLoading: dsLoading } = useDatasources();
  const [selectedIdx, setSelectedIdx] = useState<number | ''>('');
  const selected = selectedIdx === '' ? null : datasources[selectedIdx];
  const { data: impact, isLoading: impactLoading } = useDatasourceBlastRadius(
    selected
      ? {
          datasourceType: selected.datasource_type,
          server: selected.server ?? '',
          database: selected.database_name ?? '',
        }
      : null
  );

  const dsLabel = (d: DatasourceItem) => {
    const loc = [d.server, d.database_name].filter(Boolean).join(' / ');
    return `${loc || d.datasource_type}${loc ? ` (${d.datasource_type})` : ''} — ${d.model_count} model${d.model_count === 1 ? '' : 's'}`;
  };

  return (
    <Card padding="lg">
      <div className="mb-2 flex items-center gap-2">
        <Database className="h-5 w-5 text-[var(--color-accent)]" />
        <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Datasource Failure Impact</h2>
      </div>
      <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
        If an external datasource is unavailable, which datasets, workspaces, capacities, and clients are impacted — derived from collected lineage (no manual setup).
      </p>

      <div className="mb-4 max-w-2xl">
        <label className="mb-1 block text-xs font-medium text-[var(--color-text-secondary)]">Datasource</label>
        <select
          className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
          value={selectedIdx}
          onChange={(e) => setSelectedIdx(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={dsLoading}
        >
          <option value="">{dsLoading ? 'Loading datasources…' : `Select a datasource (${datasources.length})`}</option>
          {datasources.map((d, i) => (
            <option key={`${d.datasource_type}|${d.server}|${d.database_name}|${i}`} value={i}>
              {dsLabel(d)}
            </option>
          ))}
        </select>
      </div>

      {selected && impactLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
        </div>
      )}

      {selected && impact && !impactLoading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Affected Datasets" value={impact.affected_datasets} icon={<Database className="h-5 w-5 text-[var(--color-accent)]" />} />
            <StatCard label="Affected Workspaces" value={impact.affected_workspaces} icon={<Building className="h-5 w-5 text-[var(--color-status-warning)]" />} />
            <StatCard label="Affected Capacities" value={impact.affected_capacities} icon={<Server className="h-5 w-5 text-violet-500" />} />
            <StatCard label="Affected Clients" value={impact.affected_clients} icon={<Users className="h-5 w-5 text-[var(--color-status-warning)]" />} />
          </div>

          {impact.workspaces.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
              <table className="w-full text-sm">
                <thead className="border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-[var(--color-text-secondary)]">Workspace</th>
                    <th className="px-4 py-2 text-left font-medium text-[var(--color-text-secondary)]">Capacity</th>
                    <th className="px-4 py-2 text-right font-medium text-[var(--color-text-secondary)]">Datasets using it</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                  {impact.workspaces.map((w) => (
                    <tr key={w.workspace_id}>
                      <td className="px-4 py-2 font-medium text-[var(--color-text-primary)]">{w.workspace_name}</td>
                      <td className="px-4 py-2 text-[var(--color-text-secondary)]">{w.capacity_name || '—'}</td>
                      <td className="px-4 py-2 text-right text-[var(--color-text-secondary)]">{w.dataset_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

/**
 * SLA Blast Radius Dashboard
 * Displays capacity dependencies, SLA impact analysis, and failure propagation visualization
 */
export const SLABlastRadiusPage: React.FC = () => {
  const {
    hasCriticalType1Or4,
    type1Status, type4Status,
    status, statusLoading,
    alerts, alertsLoading,
  } = useSla();

  const { data: graph, isLoading: graphLoading, isError: graphError } = useDependencyNetwork();

  const isLoading = statusLoading;
  // Spec 298 FR-006 (rows 81-82): the graph tiles wait on the SLA status, not on the graph, so until the graph
  // arrives (or when its read fails) they say so instead of counting an empty graph.
  const graphTileSubtext = graph ? undefined : graphError ? 'dependency graph could not be read' : 'loading…';

  return (
    <PageLayout
      helpId="auto.s-l-a-blast-radius"
      title="SLA Blast Radius"
      description="Data-asset dependency network, SLA impact analysis, and failure impact"
      icon={<Shield className="h-6 w-6" />}
      actions={
        <ExportButton
          data={alerts ?? []}
          filename="sla-alerts"
          columns={[
            { key: 'capacity_name', label: 'Capacity Name' },
            { key: 'capacity_type', label: 'Capacity Type' },
            { key: 'severity', label: 'Severity' },
            { key: 'breach_description', label: 'Breach Description' },
            { key: 'occurred_at', label: 'Occurred At' },
          ]}
        />
      }
    >
      {isLoading ? (
        <LoadingState rows={6} />
      ) : (
        <div className="space-y-6">
          {/* Critical Banner */}
          {hasCriticalType1Or4 && (
            <Card className="flex items-center gap-3 border-[var(--color-status-danger)] bg-[var(--color-status-danger)]/10">
              <AlertTriangle className="h-6 w-6 flex-shrink-0 text-[var(--color-status-danger)]" />
              <div>
                <p className="font-bold text-[var(--color-status-danger)]">
                  CRITICAL: Capacity in Critical State
                </p>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  A Fabric or Power BI capacity is in a critical state — downstream workspaces and reports at risk.
                </p>
              </div>
            </Card>
          )}

          {/* Type 1/4 Status Cards */}
          {status && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Fabric"
                // Spec 298 FR-006 (row 79): the status has a row only per capacity type the tenant has.
                value={type1Status ? `${type1Status.healthy_count}/${type1Status.total_capacities} Healthy` : 'no Fabric capacities'}
                icon={
                  <Shield
                    className={clsx(
                      'h-5 w-5',
                      type1Status?.critical_count
                        ? 'text-[var(--color-status-danger)]'
                        : 'text-[var(--color-status-success)]',
                    )}
                  />
                }
              />
              <StatCard
                label="PowerBI"
                // Spec 298 FR-006 (row 80): as row 79, for Power BI.
                value={type4Status ? `${type4Status.healthy_count}/${type4Status.total_capacities} Healthy` : 'no Power BI capacities'}
                icon={
                  <Shield
                    className={clsx(
                      'h-5 w-5',
                      type4Status?.critical_count
                        ? 'text-[var(--color-status-danger)]'
                        : 'text-[var(--color-accent)]',
                    )}
                  />
                }
              />
              <StatCard
                label="Tracked Data Assets"
                value={graph ? graph.nodes.filter((n) => n.capacity_type === 'Lakehouse' || n.capacity_type === 'Warehouse').length : '—'}
                subtext={graphTileSubtext}
                icon={<Building className="h-5 w-5 text-[var(--color-accent)]" />}
              />
              <StatCard
                label="Dependency Links"
                value={graph ? graph.edges.length : '—'}
                subtext={graphTileSubtext}
                icon={<Server className="h-5 w-5 text-[var(--color-accent)]" />}
              />
            </div>
          )}

          {/* Compliance Overview */}
          {status && (
            <Card padding="lg">
              <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]">
                SLA Compliance by Capacity Type
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                {status.map((s) => (
                  <div
                    key={s.capacity_type}
                    className="rounded-lg border border-[var(--color-border)] p-4"
                  >
                    <div className="text-sm font-medium text-[var(--color-text-secondary)]">
                      {s.capacity_type}
                    </div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span
                        className={clsx(
                          'text-2xl font-bold',
                          s.compliance_percentage >= 90
                            ? 'text-[var(--color-status-success)]'
                            : s.compliance_percentage >= 70
                              ? 'text-[var(--color-status-warning)]'
                              : 'text-[var(--color-status-danger)]',
                        )}
                      >
                        {s.compliance_percentage}%
                      </span>
                      <span className="text-xs text-[var(--color-text-secondary)]">compliant</span>
                    </div>
                    <div className="mt-2 flex gap-2 text-xs">
                      <span className="text-[var(--color-status-success)]">{s.healthy_count} healthy</span>
                      {s.warning_count > 0 && (
                        <span className="text-[var(--color-status-warning)]">{s.warning_count} warning</span>
                      )}
                      {s.critical_count > 0 && (
                        <span className="text-[var(--color-status-danger)]">{s.critical_count} critical</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Lakehouse / Warehouse Failure Impact (real lineage data) */}
          <LakehouseWarehouseImpactPanel />

          {/* Datasource Failure Impact (real lineage data) */}
          <DatasourceImpactPanel />

          {/* Dependency Network — real: lakehouse/warehouse -> workspaces -> capacities */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Card padding="lg" className="lg:col-span-2">
              <h2 className="mb-1 text-lg font-semibold text-[var(--color-text-primary)]">Dependency Network</h2>
              <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
                Lakehouse / warehouse → the workspaces whose datasets consume it → their capacity.
              </p>
              {graphLoading ? (
                <LoadingState rows={6} />
              ) : (
                <BlastRadiusGraph nodes={graph?.nodes ?? []} edges={graph?.edges ?? []} />
              )}
            </Card>

            <Card padding="lg">
              <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]">Dependency Tree</h2>
              <DependencyMap nodes={graph?.nodes ?? []} edges={graph?.edges ?? []} />
            </Card>
          </div>

          {/* Alert Timeline */}
          <Card padding="lg">
            {/* SPEC 268 sweep, owner decision 2026-08-06: relabelled from "Recent SLA
                Alerts". The feed underneath reads dbo.alerts joined to capacities -
                it shows CAPACITY alerts, and always did. The SLA-breach table it
                appeared to represent (dbo.sla_breach_events) is retired: zero rows,
                no writer anywhere. A heading promising SLA breaches over a feed that
                cannot contain one is an assurance the platform cannot make. */}
            <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]">Open Capacity Alerts</h2>
            {alertsLoading ? <LoadingState rows={4} /> : <AlertTimeline alerts={alerts ?? []} />}
          </Card>
        </div>
      )}
    </PageLayout>
  );
};

export default SLABlastRadiusPage;
